import { useState } from 'react';
import { useAuth } from '@/features/auth/providers/AuthProvider';
import useWrapSeason from '@/features/wrap/hooks/useWrapSeason';
import { useSeasonalFlags } from '@/shared/providers/SeasonalFlagsProvider';
import { hasSeenWrap, markWrapSeen } from '@/features/wrap/utils/seen';

export interface WrapBannerState {
  year: number;
  /** True while the wrap nudge should be on screen. */
  shouldShow: boolean;
  /**
   * True while wrap may still claim the banner slot — either the nudge is
   * visible, or the season is still loading and wrap is enabled. Other banners
   * should wait until this is false.
   */
  occupiesSlot: boolean;
  dismiss: () => void;
}

/** Shared visibility for the year-in-review nudge so other banners can defer to it. */
export default function useWrapBanner(): WrapBannerState {
  const { user } = useAuth();
  const { ready, wrapEnabled } = useSeasonalFlags();
  const { isOpen, year, isLoading } = useWrapSeason(user?.uid);
  const [dismissedYear, setDismissedYear] = useState<number | null>(null);

  const shouldShow = Boolean(user) && isOpen && dismissedYear !== year && !hasSeenWrap(year);

  const dismiss = () => {
    markWrapSeen(year);
    setDismissedYear(year);
  };

  return {
    year,
    shouldShow,
    occupiesSlot: shouldShow || !ready || (wrapEnabled && isLoading),
    dismiss,
  };
}
